function fieldName(target) {
  if (!target || typeof target.getAttribute !== "function") {
    return "";
  }
  const name = target.getAttribute("name") || target.id || target.getAttribute("autocomplete") || "";
  return String(name).slice(0, 32);
}

function isPasswordField(target) {
  if (!target) {
    return false;
  }
  return (
    target.type === "password" ||
    target.autocomplete === "new-password" ||
    target.autocomplete === "current-password" ||
    target.name === "password"
  );
}

function webCrypto() {
  // globalThis is the window on the page and self inside a worker.
  // eslint-disable-next-line no-undef
  return typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
}

async function sha256Hex(text) {
  const subtle = webCrypto() && webCrypto().subtle;
  if (!subtle) {
    return "";
  }
  const buffer = await subtle.digest("SHA-256", new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function createSignalCollector() {
  const state = {
    pointer: 0,
    keys: 0,
    focus: 0,
    blur: 0,
    paste_password: false,
    fill_order: [],
    listening: false,
  };

  const onPointer = () => {
    state.pointer += 1;
  };
  const onKey = () => {
    state.keys += 1;
  };
  const remember = (target) => {
    const name = fieldName(target);
    if (!name || name === "af_hp" || state.fill_order.includes(name) || state.fill_order.length >= 10) {
      return;
    }
    state.fill_order.push(name);
  };
  const onFocus = (event) => {
    state.focus += 1;
    remember(event.target);
  };
  const onBlur = () => {
    state.blur += 1;
  };
  const onPaste = (event) => {
    if (isPasswordField(event.target)) {
      state.paste_password = true;
    }
  };

  return {
    get listening() {
      return state.listening;
    },
    start() {
      if (state.listening || typeof document === "undefined") {
        return;
      }
      document.addEventListener("pointerdown", onPointer, true);
      document.addEventListener("keydown", onKey, true);
      document.addEventListener("focusin", onFocus, true);
      document.addEventListener("focusout", onBlur, true);
      document.addEventListener("paste", onPaste, true);
      state.listening = true;
    },
    stop() {
      if (!state.listening || typeof document === "undefined") {
        return;
      }
      document.removeEventListener("pointerdown", onPointer, true);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("focusin", onFocus, true);
      document.removeEventListener("focusout", onBlur, true);
      document.removeEventListener("paste", onPaste, true);
      state.listening = false;
    },
    async snapshot() {
      const nav = typeof navigator !== "undefined" ? navigator : {};
      const scr = typeof window !== "undefined" && window.screen ? window.screen : {};
      const tz =
        typeof Intl !== "undefined" && Intl.DateTimeFormat
          ? Intl.DateTimeFormat().resolvedOptions().timeZone || ""
          : "";
      const langs = Array.from(nav.languages || []).slice(0, 8);
      const hw = Number(nav.hardwareConcurrency) || 0;
      const dpr = typeof window !== "undefined" ? Number(window.devicePixelRatio) || 1 : 1;
      const platform = nav.platform || "";
      const raw = [hw, `${scr.width || 0}x${scr.height || 0}`, dpr, tz, langs.join(","), platform].join("|");
      return {
        pointer: state.pointer,
        keys: state.keys,
        focus: state.focus,
        blur: state.blur,
        paste_password: state.paste_password,
        fill_order: state.fill_order.slice(0, 10),
        webdriver: Boolean(nav.webdriver),
        hw,
        screen: [Number(scr.width) || 0, Number(scr.height) || 0],
        dpr,
        tz,
        langs,
        touch: Number(nav.maxTouchPoints) > 0,
        fp: await sha256Hex(raw),
      };
    },
  };
}
